import {Icon, Stroke, Style, Text, Fill} from 'ol/style.js';
import {Feature} from 'ol';
import type {StyleFunction} from 'ol/style/Style';
import olStyleStyle from 'ol/style/Style.js';
import type {FeatureLike} from 'ol/Feature';
import Point from 'ol/geom/Point.js';
import olStyleStroke from 'ol/style/Stroke.js';
import type {MultiLineString} from 'ol/geom.js';
import {MultiPolygon, Polygon} from 'ol/geom.js';
import {getArrowStyle} from './arrowStyle.ts';
import {setHighlightFunctions} from '../../proxies/proxy-highlight.ts';
import type {Coordinate} from 'ol/coordinate';
import {resolveDeployPrefix} from '../../initialization/deployPrefixResolver.ts';
import {layersManager} from '../creation.ts';
import type {LandLayers} from '../../layers/layerTypes.ts';
import {getDetourIconStyle} from './detour.ts';
import type {DetourSource} from './detour.ts';
import {onlyRouteStyle} from './mvt.ts';

const HIGHLIGHT_COLOR = '#e51e1e';

const lineHighlightStyle = new Style({
  stroke: new Stroke({
    color: HIGHLIGHT_COLOR,
    width: 10,
  }),
});

const segmentHighlightStyleFar = new Style({
  stroke: new Stroke({
    color: HIGHLIGHT_COLOR,
    width: 10,
    lineDash: [0.001, 20],
  }),
});

const segmentHighlightStyleNear = new Style({
  stroke: new Stroke({
    color: HIGHLIGHT_COLOR,
    width: 14,
    lineDash: [0.001, 60],
  }),
});

const polygonHighlightNear = new olStyleStyle({
  stroke: new olStyleStroke({
    color: HIGHLIGHT_COLOR,
    width: 9,
  }),
});

const polygonHighlightFar = new olStyleStyle({
  stroke: new olStyleStroke({
    color: HIGHLIGHT_COLOR,
    width: 6,
  }),
});

const cameraStyle = new Style({
  image: new Icon({
    src: '/img/camera_focus.svg?ts11=TIMESTAMP11',
    scale: 1.5,
  }),
});

const poiHighlightSvg = `<svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 33"
    width="20px"
    height="33px"
    fill="#AD091D"
  >
    <path
      fill="currentFill"
      stroke="white"
      stroke-width="2"
      paint-order="stroke"
      d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"
      transform="translate(-8, -7)"
    />
  </svg>`;

const iconHighlightStyle: Record<string, Style> = {};

function createIconStyle(
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  src: string,
  round = false,
) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const activeColor = 'rgb(196 19 19 / 0.5)'; //set border color
  const s = 9; // thickness scale
  const x = 9; // final x position
  const y = 9; // final y position
  const dArr = [-1, -1, 0, -1, 1, -1, -1, 0, 1, 0, -1, 1, 0, 1, 1, 1]; // offset array

  //set new canvas dimensions adjusted for border
  canvas.width = image.width + s + s;
  canvas.height = image.height + s + s;

  // draw images at offsets from the array scaled by s
  for (let i = 0; i < dArr.length; i += 2)
    ctx.drawImage(image, x + dArr[i] * s, y + dArr[i + 1] * s);

  // fill with color
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = activeColor;
  if (round) {
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      Math.min(canvas.width, canvas.height) / 2,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // draw original image in normal mode
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(image, x, y, image.width, image.height);
  const img = canvas.toDataURL();

  //create new openlayers icon style from canvas
  iconHighlightStyle[src] = new Style({
    image: new Icon({
      crossOrigin: 'anonymous',
      src: img,
    }),
  });
  return iconHighlightStyle[src];
}

const deployPrefix = resolveDeployPrefix();
function getIconHighlight(feature: FeatureLike): Style {
  const props: any = feature.getProperties();
  const src: string = props.logoSrc;
  const isDetour = props.category === 'detour';
  const isTourPoi = props.type === 'POI' && !!props.tourId;
  const poiTourKey = isTourPoi ? `${props.tourId}_${props.index}` : undefined;
  const detourKey = isDetour
    ? `${props.symbol_intermediate}_${props.is_intermediate_point}_${props.symbol_start_end}_${props.is_start_end_point}`
    : undefined;
  const key = src || detourKey || poiTourKey;
  if (iconHighlightStyle[key]) {
    return iconHighlightStyle[key];
  } else if (isDetour) {
    iconHighlightStyle[detourKey] = getDetourIconStyle(feature, true);
    return iconHighlightStyle[detourKey];
  } else if (isTourPoi) {
    const label = `${props.index + 1}`;
    iconHighlightStyle[key] = new Style({
      image: new Icon({
        crossOrigin: 'anonymous',
        src: `data:image/svg+xml;base64,${btoa(poiHighlightSvg)}`,
        anchorOrigin: 'bottom-left',
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        anchor: [0.5, -0.1],
      }),
      text: new Text({
        font: 'bold 11px Inter Variable',
        text: label,
        offsetY: -25,
        fill: new Fill({
          color: '#fff',
        }),
      }),
    });
    return iconHighlightStyle[key];
  } else if (!src) {
    const balloonSrc = `${deployPrefix}/img/balloon.png`;
    if (iconHighlightStyle[balloonSrc]) return iconHighlightStyle[balloonSrc];
    iconHighlightStyle[balloonSrc] = new Style({
      image: new Icon({
        crossOrigin: 'anonymous',
        src: balloonSrc,
        anchor: [0.5, 0.5],
      }),
    });
    return iconHighlightStyle[balloonSrc];
  } else if (src === `${deployPrefix}/img/pointer_cross2.png`) {
    iconHighlightStyle[src] = new Style({
      image: new Icon({
        crossOrigin: 'anonymous',
        src: src,
        anchor: [0.5, 0.5],
      }),
    });
    return iconHighlightStyle[src];
  } else {
    const image = new Icon({src}).getImage(1);
    let round = false;
    if (src.includes('Place_22zoom.svg')) round = true;

    // If image is not loaded yet, listen to load event to update layer style later
    if (!image.width && image instanceof HTMLImageElement) {
      image.addEventListener(
        'load',
        () => {
          createIconStyle(image, src, round);
          layersManager.highlightLayer.setStyle(getHighlightStyle());
        },
        {once: true},
      );
    }
    return createIconStyle(image, src, round);
  }
}

function getDetourFeaturesById(id: string, land: LandLayers): Feature[] {
  const features: Feature[] = [];
  const detourLayers = layersManager.detourLayers[land]?.getLayersArray() || [];
  detourLayers.forEach((l) => {
    (<DetourSource>l.getSource()).getFeatures().forEach((f) => {
      // do not highlight closed way line
      if (f.get('id') === id && !f.get('is_closed_way')) {
        f.set('category', 'detour');
        features.push(f);
      }
    });
  });
  return features;
}

// Highlight a feature, if `persistent` is true, the feature will not be cleared when `clearHighlight` is called except when `force` is true
// A persistent feature can only be removed by calling `unhighlight`
export function highlight(feature: Feature, persistent: boolean) {
  const features: Feature[] = [];
  const props = feature.getProperties();
  if (props['category'] === 'slowup') return;
  const highlightSource = layersManager.highlightLayer.getSource();
  if (props['category'] === 'detour') {
    let land = props['land'];
    if (!land?.length && props['hiking_network']) land = 'wanderland';
    features.push(...getDetourFeaturesById(props['id'], land));
  }
  if (!features.length) features.push(feature);
  features.forEach((f) => {
    f.set('persistent_highlight', persistent);
    if (!highlightSource.hasFeature(f)) highlightSource.addFeature(f);
  });
}

export function unhighlight(feature: Feature) {
  const features: Feature[] = [feature];
  const props = feature.getProperties();
  if (props['category'] === 'detour') {
    let land = props['land'];
    if (!land?.length && props['hiking_network']) land = 'wanderland';
    features.push(...getDetourFeaturesById(props['id'], land));
  }
  features.forEach((f) =>
    layersManager.highlightLayer.getSource().removeFeature(f),
  );
}

// FIXME: only one point can be highlighted at a time
const highlightPositionFeature = new Feature({
  visible: false,
});

export function highlightPosition(
  coordinate: Coordinate,
  iconFileName?: string,
): Feature {
  highlightPositionFeature.setGeometry(new Point(coordinate));
  highlightPositionFeature.set(
    'logoSrc',
    iconFileName ? `${deployPrefix}/img/${iconFileName}` : undefined,
  );
  highlight(highlightPositionFeature, false);
  return highlightPositionFeature;
}

export function getHighlightStyle(): StyleFunction {
  return function (feature: FeatureLike, resolution: number): Style | Style[] {
    if (feature.get('name') === 'focused-photo') {
      return cameraStyle;
    }
    if (feature.get('type') === 'wildlife') {
      return lineHighlightStyle;
    }
    const geom = feature.getGeometry();
    if (geom instanceof Point) {
      return getIconHighlight(feature);
    }
    if (geom instanceof Polygon || geom instanceof MultiPolygon) {
      return resolution <= 20 ? polygonHighlightNear : polygonHighlightFar;
    }
    if (feature.get('arrow_highlight')) {
      return getArrowStyle(feature as Feature<MultiLineString>, resolution);
    }

    const highlightStyle =
      resolution <= 20 ? segmentHighlightStyleNear : segmentHighlightStyleFar;
    if (feature.get('onlyRoute')) {
      highlightStyle.setZIndex(50);
      const routeStyles = onlyRouteStyle(feature, resolution);
      return Array.isArray(routeStyles)
        ? [highlightStyle, ...(<Style[]>onlyRouteStyle(feature, resolution))]
        : [highlightStyle, <Style>onlyRouteStyle(feature, resolution)];
    } else {
      return highlightStyle;
    }
  };
}

export function clearHighlight(force: boolean) {
  const source = layersManager.highlightLayer.getSource();
  if (force) {
    source.clear();
  } else {
    source.getFeatures().forEach((feature) => {
      if (feature.get('persistent_highlight') === false) {
        source.removeFeature(feature);
      }
    });
  }
}

export function initializeHighlight() {
  setHighlightFunctions({
    clearHighlight,
    highlight,
    unhighlight,
    highlightPosition,
  });
}
